﻿<template>
  <div class="ms-input ms-editor w-100" :class="{ 'ms-validate': isValidate }">
    <label class="label" v-if="label">{{ label }}</label>
    <div
      class="flex-row"
      :class="[
        { error: errorMessage, disabled: disabled },
        hasBorder ? 'border' : '',
      ]"
    >
      <div
        :class="['icon24 icon left', leftIcon]"
        v-if="leftIcon"
        :title="errorProvider.message"
      ></div>

      <input
        v-if="trimModel"
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        v-model.trim="internalText"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        ref="input"
        :class="{ disabled: disabled }"
        v-on="listeners"
      />

      <input
        v-else
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        v-model="internalText"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        ref="input"
        :class="{ disabled: disabled }"
        v-on="listeners"
      />

      <div class="ic-right" v-if="rightIcon">
        <div
          :class="['icon24 icon', rightIcon]"
          @click="onClickRightIcon"
        ></div>
      </div>
    </div>
    <div class="error-text" v-if="errorMessage">
      {{ errorMessage }}
    </div>
  </div>
</template>
<script>
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
  nextTick,
  getCurrentInstance,
} from 'vue';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import { useValidateControl } from '@/setup/validateControl.js';
export default defineComponent({
  extends: msBaseComponent,
  name: 'msInput',
  props: {
    /**
     * V-model bắn ra ngoài binding 2 chiều
     */
    modelValue: {
      default: null,
    },
    /**
     * Hiện thị chũ mờ ở dưới ô input
     * Created by LTDAT(22.06.2020)
     */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    //icon left
    leftIcon: {
      default: null,
      type: String,
    },
    //icon right
    rightIcon: {
      default: null,
      type: String,
    },
    //Giới hạn nhập của ô input
    maxLength: {
      default: null,
      type: Number,
    },
    //disabled
    disabled: {
      default: false,
      type: Boolean,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    /*
     * Hàm show ra câu thông báo riêng theo từng trường hợp
     * pvduy 18/03/2021
     * ví dụ: customWarring {rules:'required', mes: 'Tài sản này là của pvduy'}
     */
    customWarring: {
      type: Object,
      default: null,
    },
    /**
     * Có được click icon hay không?
     * NNLINH 27.06.2022
     */
    isClickRightIcon: {
      default: false,
      type: Boolean,
    },
    trimModel: {
      default: false,
      type: Boolean,
    },
  },
  emits: [
    'msKeydown',
    'update:modelValue',
    'focus',
    'blur',
    'change',
    'msKeyup',
    'clickRightIcon',
  ],
  setup(props, { emit }) {
    //Variable
    let internalText = ref('');
    let focused = false;
    let displayValue = null;
    let scrollWidth = 0;
    let input = ref(null);
    const { errorMessage, validate, isValidate, clearValidate } =
      useValidateControl({
        props,
      });

    const { proxy } = getCurrentInstance();

    //Method
    const hasTooltip = () => {
      if (scrollWidth > input?.value?.offsetWidth) {
        return true;
      }
      return false;
    };

    //Sự kiện focus vào ô input
    const onFocus = (e) => {
      focused = true;
      emit('focus', internalText.value, e);
    };

    //Sự kiên blur ra ngoài ô input
    const onBlur = (e) => {
      focused = false;
      validate();
      emit('blur', internalText.value, e);
    };

    /**
     * Sự kiện Click icon bên phải ô Input
     * NNLINH 27.06.2022
     */
    const onClickRightIcon = () => {
      if (props.isClickRightIcon) {
        emit('clickRightIcon');
      }
    };

    //Reset lại giá trị về ban đầu
    const reset = () => {
      internalText.value = '';
      displayValue = null;
    };
    //Set giá trị cho ô input
    const setValue = (value) => {
      if (proxy.rules) {
        const rule = proxy.rules.find(
          (x) => x.name === 'pattern' && x.compareValue === 'phone'
        );
        if (rule && value) {
          value = value.replace(/ /g, '').replace(/\./g, '');
        }
      }
      internalText.value = value;
    };

    //Lấy giá trị ô input
    const getValue = () => {
      return internalText.value;
    };

    /**
     * Hủy lan rộng event
     * HTHIEP 09.02.2022
     */
    const cancelEvent = (e) => {
      if (e) {
        if (typeof e.preventDefault === 'function') {
          e.preventDefault();
        }

        if (typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }

        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
      }
    };

    //Hooks
    //set internalText
    onMounted(() => {
      internalText.value = props.modelValue;
    });

    /**
     * Lắng nghe event => thò ra ngoài
     * HTHIEP 11.02.2022: Làm mịn, migration sang vue 3
     */
    const listeners = computed(() => {
      return {
        blur: (e) => {
          cancelEvent(e);
          onBlur(e);
        },
        focus: (e) => {
          cancelEvent(e);
          onFocus(e);
        },
        change: (e) => {
          cancelEvent(e);
          emit('change', e, internalText.value);
        },
        keydown: (e) => {
          e.stopPropagation();
          emit(proxy.$ms.constant.CustomEvent.MsKeydown, e, internalText.value);
        },
        keyup: (e) => {
          emit('msKeyup', e, internalText.value);
        },
      };
    });

    /**
     * Lắng nghe xử lý update lại modelValue
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => internalText.value,
      (value) => {
        if (value !== props.modelValue) {
          emit('update:modelValue', value);
          scrollWidth = input.value.scrollWidth;
        }
      }
    );

    /**
     * Lắng nghe thay đổi modelValue set lại text cho input
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => props.modelValue,
      (value) => {
        setValue(value);
      }
    );
    return {
      internalText,
      focused,
      listeners,
      reset,
      hasTooltip,
      displayValue,
      input,
      getValue,
      errorMessage,
      validate,
      isValidate,
      clearValidate,
      onClickRightIcon,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsInput.scss';
.ic-right {
  display: none;
  align-items: center;
  width: $input-height;
  justify-content: center;
  border-left: 1px solid $secondary;
}
</style>
